import React, { Component } from "react";

const COLOURS = [
  "#1B4F8A",
  "#E67E22",
  "#1ABC9C",
  "#E91E8C",
  "#C9A227",
  "#5DADE2",
  "#8B6914",
  "#8E44AD",
];

const ACCENT = "#1B4F8A";
const TEXT = "#2c3e50";
const TEXT_MUTED = "#8a9aac";
const VALUE_BLUE = "#2B6CB0";
const BORDER = "#d0d7de";
const BORDER_FOCUS = "#1a2332";

/** Ratio 1:N means N parts water to 1 part chemical → stored as (1000/N) ml/L.
 *  Example: 1:1000 ↔ 1 ml/L. */
function isRatioUnit(units) {
  return units && String(units.c).toLowerCase() === "ratio";
}

function mlPerLToRatioWater(mlPerL) {
  const v = Number(mlPerL);
  if (!Number.isFinite(v) || v <= 0) return null;
  return 1000 / v;
}

function ratioWaterToMlPerL(water) {
  const w = Number(water);
  if (!Number.isFinite(w) || w <= 0) return 0;
  return 1000 / w;
}

function formatRatioWater(water) {
  if (water == null) return "";
  const n = Number(water);
  if (!Number.isFinite(n)) return "";
  const rounded = Math.round(n * 1000) / 1000;
  if (Number.isInteger(rounded)) return String(rounded);
  return String(rounded);
}

function formatConcDisplay(conc, units) {
  const v = Number(conc);
  if (!Number.isFinite(v) || v === 0) return "0";
  if (isRatioUnit(units)) {
    const water = mlPerLToRatioWater(v);
    if (water == null) return "0";
    return `1:${formatRatioWater(water)}`;
  }
  const rounded = Math.round(v * 1000) / 1000;
  return String(rounded);
}

const cardBase = {
  fontFamily: "system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  background: "#fff",
  borderRadius: 12,
  padding: 16,
  boxSizing: "border-box",
  width: "100%",
  height: "100%",
};

const headerRow = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 14,
};

const colourDot = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  flexShrink: 0,
};

const nameStatus = {
  flex: 1,
  fontSize: 18,
  fontWeight: 700,
  color: TEXT,
  minWidth: 0,
};

const nameInput = {
  flex: 1,
  minWidth: 0,
  fontSize: 16,
  padding: "8px 10px",
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  fontFamily: "inherit",
  color: TEXT,
};

const nutrientRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 10,
};

const nutrientName = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: TEXT,
  lineHeight: 1.3,
};

const valueStatus = {
  flexShrink: 0,
  fontSize: 14,
  fontWeight: 600,
  color: VALUE_BLUE,
  fontVariantNumeric: "tabular-nums",
};

const valueStatusZero = {
  ...valueStatus,
  color: TEXT_MUTED,
  fontWeight: 500,
};

const scheduledBadge = {
  flexShrink: 0,
  fontSize: 11,
  fontWeight: 600,
  color: "#fff",
  background: "#0d7377",
  borderRadius: 6,
  padding: "3px 8px",
  letterSpacing: "0.02em",
};

const ratioEditWrap = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  flexShrink: 0,
};

const ratioFixed = {
  width: 36,
  height: 32,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
  border: `1px solid ${BORDER}`,
  background: "#f7f8fa",
  color: TEXT_MUTED,
  fontSize: 13,
  fontVariantNumeric: "tabular-nums",
  userSelect: "none",
};

const ratioInput = {
  width: 72,
  height: 32,
  padding: "0 6px",
  borderRadius: 6,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  fontSize: 13,
  fontFamily: "inherit",
  color: TEXT,
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
};

const concInput = {
  width: 88,
  height: 32,
  padding: "0 6px",
  borderRadius: 6,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  fontSize: 13,
  fontFamily: "inherit",
  color: TEXT,
  fontVariantNumeric: "tabular-nums",
  textAlign: "right",
};

const legend = {
  textAlign: "right",
  fontSize: 11,
  color: TEXT_MUTED,
  marginTop: 2,
  marginBottom: 14,
};

const colourLabel = {
  fontSize: 12,
  color: TEXT_MUTED,
  marginBottom: 8,
};

const swatchRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginBottom: 14,
};

const notesInput = {
  width: "100%",
  minHeight: 40,
  padding: "10px 12px",
  borderRadius: 8,
  border: `1px solid ${BORDER}`,
  boxSizing: "border-box",
  fontSize: 13,
  fontFamily: "inherit",
  color: TEXT,
  marginBottom: 16,
  resize: "vertical",
};

const notesStatus = {
  fontSize: 12,
  color: TEXT_MUTED,
  marginTop: 10,
  marginBottom: 4,
  whiteSpace: "pre-wrap",
  fontStyle: "italic",
};

const footerRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const btnBase = {
  padding: "8px 16px",
  borderRadius: 8,
  fontSize: 14,
  fontFamily: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};

const btnSave = {
  ...btnBase,
  background: ACCENT,
  color: "#fff",
  border: `1px solid ${ACCENT}`,
};

const btnCancel = {
  ...btnBase,
  background: "#fff",
  color: TEXT,
  border: `1px solid ${BORDER}`,
  fontWeight: 500,
};

const btnDelete = {
  ...btnBase,
  background: "#fff",
  color: "#c62828",
  border: "1px solid #e57373",
  fontWeight: 500,
  marginLeft: "auto",
};

const editBtn = {
  background: "none",
  border: "none",
  padding: 4,
  cursor: "pointer",
  color: VALUE_BLUE,
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
};

function PencilIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.04a1.003 1.003 0 0 0 0-1.42l-2.5-2.5a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.999-1.66z" />
    </svg>
  );
}

export default class DoserRecipePane extends Component {
  emit(payload) {
    const { onChange } = this.props;
    if (typeof onChange === "function") {
      onChange(payload);
    }
  }

  handleConcChange(nute, raw, asRatio) {
    if (raw === "") {
      this.emit({
        type: "conc",
        index: nute.concIndex != null ? nute.concIndex : nute.index,
        value: 0,
      });
      return;
    }
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    const value = asRatio ? ratioWaterToMlPerL(num) : num;
    this.emit({
      type: "conc",
      index: nute.concIndex != null ? nute.concIndex : nute.index,
      value,
    });
  }

  renderNutrientRows(editable) {
    const { nutrients, units } = this.props;
    const list = Array.isArray(nutrients) ? nutrients : [];
    const ratio = isRatioUnit(units);

    return list.map((nute, i) => {
      const name = nute.name != null ? nute.name : nute.nutrientName;
      const conc = nute.conc != null ? nute.conc : nute.concentration;
      const key = nute.key != null ? nute.key : (nute.concIndex != null ? nute.concIndex : i);
      const nuteRef = { ...nute, index: i };

      if (!editable) {
        const display = formatConcDisplay(conc, units);
        const isZero = display === "0";
        return (
          <div key={key} style={nutrientRow}>
            <div style={nutrientName}>{name || "—"}</div>
            <div style={isZero ? valueStatusZero : valueStatus}>{display}</div>
          </div>
        );
      }

      if (ratio) {
        const water = mlPerLToRatioWater(conc);
        return (
          <div key={key} style={nutrientRow}>
            <div style={nutrientName}>{name || "—"}</div>
            <div style={ratioEditWrap}>
              <span style={ratioFixed} aria-hidden="true">
                1
              </span>
              <span style={{ color: TEXT_MUTED }}>:</span>
              <input
                type="number"
                step="any"
                min={0}
                style={ratioInput}
                value={water == null ? "" : formatRatioWater(water)}
                onChange={(e) => this.handleConcChange(nuteRef, e.target.value, true)}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = BORDER_FOCUS;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = BORDER;
                }}
                aria-label={`${name || "Nutrient"} water ratio`}
              />
            </div>
          </div>
        );
      }

      return (
        <div key={key} style={nutrientRow}>
          <div style={nutrientName}>{name || "—"}</div>
          <input
            type="number"
            step="any"
            min={0}
            style={concInput}
            value={conc == null || conc === "" ? "" : conc}
            onChange={(e) => this.handleConcChange(nuteRef, e.target.value, false)}
            aria-label={`${name || "Nutrient"} concentration`}
          />
        </div>
      );
    });
  }

  renderColourPicker() {
    const { color } = this.props;
    const selected = color || COLOURS[0];
    return (
      <div>
        <div style={colourLabel}>Colour</div>
        <div style={swatchRow}>
          {COLOURS.map((c) => {
            const isSel = c.toLowerCase() === String(selected).toLowerCase();
            return (
              <button
                key={c}
                type="button"
                title={c}
                aria-label={`Colour ${c}`}
                aria-pressed={isSel}
                onClick={() => this.emit({ type: "color", value: c })}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: c,
                  border: isSel ? `3px solid ${BORDER_FOCUS}` : "2px solid transparent",
                  boxShadow: isSel ? `0 0 0 1px ${BORDER_FOCUS}` : "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              />
            );
          })}
        </div>
      </div>
    );
  }

  renderStatus() {
    const { name, color, notes, units, scheduled } = this.props;
    const ratio = isRatioUnit(units);
    return (
      <div style={{ ...cardBase, border: `1px solid ${BORDER}` }}>
        <div style={headerRow}>
          <div
            style={{ ...colourDot, background: color || COLOURS[0] }}
            aria-hidden="true"
          />
          <div style={nameStatus}>{name != null && name !== "" ? name : "—"}</div>
          {scheduled ? <span style={scheduledBadge}>Scheduled</span> : null}
          <button
            type="button"
            style={editBtn}
            title="Edit recipe"
            aria-label="Edit recipe"
            onClick={() => this.emit({ type: "edit" })}
          >
            <PencilIcon />
          </button>
        </div>

        {this.renderNutrientRows(false)}
        {ratio && <div style={legend}>chemical : water</div>}

        {notes != null && String(notes).trim() !== "" && (
          <div style={notesStatus}>{notes}</div>
        )}
      </div>
    );
  }

  renderConfig() {
    const { name, color, notes, units } = this.props;
    const ratio = isRatioUnit(units);
    return (
      <div style={{ ...cardBase, border: `1px solid ${ACCENT}` }}>
        <div style={headerRow}>
          <div
            style={{ ...colourDot, background: color || COLOURS[0] }}
            aria-hidden="true"
          />
          <input
            type="text"
            style={nameInput}
            value={name == null ? "" : name}
            onChange={(e) => this.emit({ type: "name", value: e.target.value })}
            aria-label="Recipe name"
          />
        </div>

        {this.renderNutrientRows(true)}
        {ratio && <div style={legend}>chemical : water</div>}

        {this.renderColourPicker()}

        <textarea
          style={notesInput}
          value={notes == null ? "" : notes}
          placeholder="Notes — crop stage, source mix, cautions..."
          onChange={(e) => this.emit({ type: "notes", value: e.target.value })}
          rows={2}
        />

        <div style={footerRow}>
          <button
            type="button"
            style={btnSave}
            onClick={() => this.emit({ type: "save" })}
          >
            Save
          </button>
          <button
            type="button"
            style={btnCancel}
            onClick={() => this.emit({ type: "cancel" })}
          >
            Cancel
          </button>
          <button
            type="button"
            style={btnDelete}
            onClick={() => this.emit({ type: "delete" })}
          >
            Delete
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { mode } = this.props;
    if (mode === "config") {
      return this.renderConfig();
    }
    return this.renderStatus();
  }
}

export {
  COLOURS as RECIPE_COLOURS,
  isRatioUnit,
  mlPerLToRatioWater,
  ratioWaterToMlPerL,
  formatConcDisplay,
};
